import { memo } from "react";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { useGetActivityReferral } from "@/activity-feed/query";
import { formatDate } from "@/core/utils/date";
import { formatMoney } from "@/core/utils/money";
import { getCustomerNameOrPhone } from "@/customer/utils";
import { SPIFF_RULE_EVENTS } from "@/spiff-rules/constants";
import { DetailCard, InfoPair } from "./common";

interface ClosedReferralDetailCardProps {
  referralId: string;
  inView: boolean;
}

export const ClosedReferralDetailCard = memo(
  ({ referralId, inView }: ClosedReferralDetailCardProps) => {
    const { data: referral, isLoading } = useGetActivityReferral(
      referralId,
      inView,
    );

    // Show loading only if we don't have data yet
    if (!referral && (!inView || isLoading)) {
      return (
        <DetailCard eventType={SPIFF_RULE_EVENTS.referral_completion}>
          <Stack gap="sm" fullWidth>
            <Skeleton className="h-5 w-48" />
            <div className="flex flex-wrap gap-x-10 gap-y-4">
              <Skeleton className="h-12 w-32" />
              <Skeleton className="h-12 w-32" />
              <Skeleton className="h-12 w-32" />
              <Skeleton className="h-12 w-32" />
            </div>
          </Stack>
        </DetailCard>
      );
    }

    if (!referral) {
      return (
        <DetailCard eventType={SPIFF_RULE_EVENTS.referral_completion}>
          <Stack gap="sm" fullWidth>
            <Typography variant="subtitleW2">Referral Details</Typography>
            <Typography variant="bodyW3" textColor="secondary">
              Referral information unavailable
            </Typography>
          </Stack>
        </DetailCard>
      );
    }

    return (
      <DetailCard eventType={SPIFF_RULE_EVENTS.referral_completion}>
        <Stack gap="sm" fullWidth>
          <Typography variant="subtitleW2">
            {getCustomerNameOrPhone(referral.potential_customer)}
          </Typography>
          <div className="flex flex-wrap gap-x-10 gap-y-4">
            <InfoPair label="Service" value={referral.service_needed} />
            <InfoPair
              label="Job Completed"
              value={formatDate(referral.appointment_date)}
            />
            <InfoPair
              label="Deal Size"
              value={formatMoney(referral.deal_size)}
            />
            <InfoPair
              label="Attributed"
              value={getCustomerNameOrPhone(referral.advocate)}
            />
          </div>
        </Stack>
      </DetailCard>
    );
  },
);

ClosedReferralDetailCard.displayName = "ClosedReferralDetailCard";
